import { LiveDataStatus } from "@/components/LiveDataStatus";
import { useProviderLiveTerms, type ProviderEntityType, type ProviderLiveTerms } from "@/hooks/useProviderLiveTerms";
import { getPreferredRateLabel, getUsableLiveTerms } from "@/utils/providerLiveTerms";

interface LenderLiveDataStripProps {
  entityType: ProviderEntityType;
  entitySlug: string;
  className?: string;
  showTerms?: boolean;
  liveTermsOverride?: ProviderLiveTerms | null;
}

export function LenderLiveDataStrip({
  entityType,
  entitySlug,
  className = "",
  showTerms = true,
  liveTermsOverride,
}: LenderLiveDataStripProps) {
  const shouldFetchLiveTerms = liveTermsOverride === undefined;
  const { data: fetchedLiveTerms } = useProviderLiveTerms(entityType, entitySlug, { enabled: shouldFetchLiveTerms });
  const liveTerms = shouldFetchLiveTerms ? fetchedLiveTerms : liveTermsOverride;
  const usableLiveTerms = getUsableLiveTerms(liveTerms);
  const rateLabel = getPreferredRateLabel(usableLiveTerms);
  const shouldCenter = className.includes("justify-center");
  const highlights = [
    { label: "Belopp", value: usableLiveTerms?.loan_amount_label },
    { label: "Ränta", value: rateLabel },
    { label: "Löptid", value: usableLiveTerms?.term_label },
    { label: "Kreditkoll", value: usableLiveTerms?.credit_check_label },
  ].filter((item) => item.value);
  const hasStatusDetail = Boolean(
    liveTerms?.status === "fresh" || liveTerms?.last_verified_at || liveTerms?.last_scraped_at || liveTerms?.source_name
  );
  const hasTermHighlights = Boolean(showTerms && usableLiveTerms && highlights.length > 0);

  if (!hasStatusDetail && !hasTermHighlights) {
    return null;
  }

  return (
    <div className={`flex flex-col gap-2 ${shouldCenter ? "items-center" : ""} ${className}`}>
      <LiveDataStatus terms={liveTerms} compact className={shouldCenter ? "justify-center" : ""} />
      {showTerms && usableLiveTerms && highlights.length > 0 && (
        <dl className={`grid w-full max-w-3xl grid-cols-2 gap-2 sm:grid-cols-4 ${shouldCenter ? "mx-auto" : ""}`}>
          {highlights.map((item) => (
            <div key={item.label} className="rounded-lg border border-border/70 bg-background/80 px-3 py-2">
              <dt className="text-[11px] font-medium uppercase text-muted-foreground">{item.label}</dt>
              <dd className="mt-1 text-sm font-semibold leading-tight text-foreground">{item.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
